import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useHistory } from "react-router-dom";
import {
  Bar,
  BarChart,
  CartesianGrid,
  ComposedChart,
  Legend,
  Line,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import {
  CalendarOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  FileTextOutlined,
  HourglassOutlined,
  InfoCircleOutlined,
  ArrowLeftOutlined,
  ReloadOutlined,
  SyncOutlined,
} from "@ant-design/icons";
import { BaseUrlRace } from "../../../../Api/BaseUrl";
import raceLogo from "../../../../assets/img/locorace.png";
import "./NationalOperationDashboard.css";

const emptyDashboard = {
  date: "",
  generatedAt: "",
  lastSync: "",
  liveFeed: [],
  kpi: {
    sjDibuatHariIni: 0,
    sjSelesaiHariIni: 0,
    pendingHariIni: 0,
  },
  trendSj: [],
  trendSdm: [],
  fleetTrend: [],
  hourly: [],
  branches: [],
  totals: {
    cabang: 0,
    jumlah: 0,
    onProcess: 0,
    onPickup: 0,
    onDelivery: 0,
    unloading: 0,
    success: 0,
    pendingPickup: 0,
    pendingBongkar: 0,
    docReturn: 0,
    docComplete: 0,
    unitBertugas: 0,
    driverMasuk: 0,
    helperMasuk: 0,
    sjDibuat: 0,
    sjBerjalan: 0,
    sjSelesai: 0,
    pendingMuat: 0,
    pendingTotal: 0,
    persenSelesai: 0,
  },
};

const numberFormatter = new Intl.NumberFormat("id-ID");

const colors = {
  success: "#4bc344",
  pending: "#ff3b30",
  average: "#1594ff",
  driver: "#178cff",
  helper: "#ffae1a",
  motor: "#18b7c9",
  grandmax: "#59c746",
  cdd: "#ffc02d",
  cddLong: "#7b4ed8",
  tronton: "#ff4038",
};

const statusClassMap = {
  critical: "is-critical",
  warning: "is-warning",
  attention: "is-attention",
  good: "is-good",
};

const formatNumber = (value) => numberFormatter.format(Number(value || 0));

const formatPercent = (value) =>
  `${Number(value || 0).toFixed(1).replace(".", ",")}%`;

const toDate = (value) => {
  if (!value) return null;
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

const formatDateLong = (value) => {
  const date = toDate(value);
  if (!date) return "-";
  return date.toLocaleDateString("id-ID", {
    day: "2-digit",
    month: "long",
    year: "numeric",
    timeZone: "Asia/Jakarta",
  });
};

const formatDayName = (value) => {
  const date = toDate(value);
  if (!date) return "-";
  return date.toLocaleDateString("id-ID", {
    weekday: "long",
    timeZone: "Asia/Jakarta",
  });
};

const formatTime = (value) => {
  const date = value instanceof Date ? value : toDate(value);
  if (!date) return "-";
  return `${date.toLocaleTimeString("id-ID", {
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
    timeZone: "Asia/Jakarta",
  })} WIB`;
};

const formatTimeWithSeconds = (value) => {
  const date = value instanceof Date ? value : toDate(value);
  if (!date) return "-";
  return `${date.toLocaleTimeString("id-ID", {
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: false,
    timeZone: "Asia/Jakarta",
  })} WIB`;
};

const getLastUpdate = (value) => {
  const time = formatTime(value);
  return time === "-" ? "-" : time;
};

const DashboardTooltip = ({ active, payload, label }) => {
  if (!active || !payload || payload.length === 0) return null;

  return (
    <div className="nod-tooltip">
      <div className="nod-tooltip-title">{label}</div>
      {payload.map((item) => (
        <div className="nod-tooltip-row" key={item.dataKey}>
          <span>
            <i style={{ backgroundColor: item.color }} />
            {item.name}
          </span>
          <strong>{formatNumber(item.value)}</strong>
        </div>
      ))}
    </div>
  );
};

const KpiCard = ({ icon, tone, title, value, subtitle }) => (
  <div className="nod-kpi-card">
    <div className={`nod-kpi-icon ${tone}`}>{icon}</div>
    <div>
      <div className="nod-kpi-title">{title}</div>
      <div className="nod-kpi-value">{formatNumber(value)}</div>
      <div className="nod-kpi-subtitle">{subtitle}</div>
    </div>
  </div>
);

const Panel = ({ title, subtitle, children }) => (
  <section className="nod-panel">
    <div className="nod-panel-header">
      <div>
        <h3>
          {title} <InfoCircleOutlined />
        </h3>
        {subtitle ? <p>{subtitle}</p> : null}
      </div>
    </div>
    {children}
  </section>
);

const StatusDot = ({ status }) => (
  <span className={`nod-status-dot ${statusClassMap[status] || ""}`} />
);

const NationalOperationDashboard = () => {
  const [dashboard, setDashboard] = useState(emptyDashboard);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [clockNow, setClockNow] = useState(new Date());
  const history = useHistory();

  const handleBack = () => {
    if (window.history.length > 1) {
      history.goBack();
      return;
    }

    history.push("/Dashboard");
  };

  const fetchDashboard = useCallback(async () => {
    setError("");
    try {
      let localToken = localStorage.getItem("token");
      let smartTvToken = "";

      const isSmartTvMode = window.location.search.includes("token");

      // Jika tidak ada token login, ATAU diakses dengan ?token= di URL (mode Smart TV)
      if (!localToken || isSmartTvMode) {
        const tokenRes = await axios.get(
          "https://api-portal.eurekagroup.id/api/smart-tv/public/token?source=smartrace"
        );
        smartTvToken = tokenRes.data?.data?.token || "";
      }

      let apiUrl = `${BaseUrlRace}sp/national-operation-dashboard`;
      if (smartTvToken) {
        apiUrl += `?token=${smartTvToken}`;
      }

      const response = await axios.get(apiUrl, {
        headers: {
          "Content-Type": "application/json",
          // Jika mode Smart TV, pastikan hapus header Authorization (undefined akan mengabaikan axios.defaults)
          Authorization: (localToken && !isSmartTvMode) ? localToken : undefined,
        },
      });
      setDashboard(response.data?.data || emptyDashboard);
    } catch (err) {
      setError(
        err?.response?.data?.status?.message ||
          err?.message ||
          "Gagal mengambil data dashboard"
      );
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchDashboard();
    const refreshId = window.setInterval(fetchDashboard, 60000);
    return () => window.clearInterval(refreshId);
  }, [fetchDashboard]);

  useEffect(() => {
    const clockId = window.setInterval(() => setClockNow(new Date()), 1000);
    return () => window.clearInterval(clockId);
  }, []);

  const chartData = useMemo(
    () =>
      (dashboard.trendSj || []).map((item) => ({
        ...item,
        rataRata30Hari: Number(item.rataRata30Hari || 0),
      })),
    [dashboard.trendSj],
  );

  const totals = dashboard.totals || emptyDashboard.totals;
  const kpi = dashboard.kpi || emptyDashboard.kpi;

  return (
    <div className="nod-page">
      {error ? (
        <div className="nod-error">
          <strong>Dashboard belum bisa dimuat.</strong>
          <span>{error}</span>
          <button type="button" onClick={fetchDashboard}>
            Coba lagi
          </button>
        </div>
      ) : null}

      <div className="nod-scale">
        <header className="nod-topbar">
          <div className="nod-brand-block">
            <button
              className="nod-menu-button"
              type="button"
              onClick={handleBack}
              aria-label="back"
            >
              <ArrowLeftOutlined />
            </button>
            <div className="nod-wordmark">
              <img src={raceLogo} alt="RACE Raja Cepat" />
            </div>
            <div className="nod-divider" />
            <h1>NATIONAL OPERATION DASHBOARD</h1>
          </div>

          <div className="nod-top-meta">
            <span>
              <CalendarOutlined />
              {formatDateLong(dashboard.date)}
            </span>
            <span>{formatDayName(dashboard.date)}</span>
            <span>
              <ClockCircleOutlined />
              {formatTime(clockNow)}
            </span>
            <span className="nod-last-sync-mini">
              <i />
              <b>LIVE</b>
              {formatTimeWithSeconds(dashboard.lastSync)}
            </span>
            <button
              className="nod-refresh"
              type="button"
              onClick={fetchDashboard}
              aria-label="refresh dashboard"
            >
              <ReloadOutlined spin={loading} />
            </button>
          </div>
        </header>

        <section className="nod-summary-grid">
          <div className="nod-kpi-strip">
            <KpiCard
              icon={<FileTextOutlined />}
              tone="blue"
              title="SJ DIBUAT HARI INI"
              value={kpi.sjDibuatHariIni}
              subtitle="Surat Jalan"
            />
            <KpiCard
              icon={<CheckCircleOutlined />}
              tone="green"
              title="SJ SELESAI HARI INI"
              value={kpi.sjSelesaiHariIni}
              subtitle="Selesai di tempat tujuan"
            />
            <KpiCard
              icon={<HourglassOutlined />}
              tone="amber"
              title="PENDING HARI INI"
              value={kpi.pendingHariIni}
              subtitle="Muat / bongkar"
            />
          </div>

          <div className="nod-sync-card nod-live-card">
            <div className="nod-live-header">
              <span>LAST ACTIVITIES</span>
              <small>5 aktivitas terbaru</small>
            </div>
            <div className="nod-live-list">
              {(dashboard.liveFeed || []).map((item) => (
                <div
                  className="nod-live-item"
                  key={item.id}
                  title={[
                    formatTime(item.createdAt),
                    item.status,
                    item.sj,
                    item.cabang,
                    item.driver,
                    item.noPolisi,
                    item.sekolah,
                  ]
                    .filter(Boolean)
                    .join(" - ")}
                >
                  <time>{formatTime(item.createdAt)}</time>
                  <span className="nod-live-status">{item.status}</span>
                  <span className="nod-live-code">{item.sj}</span>
                  <span className="nod-live-text">{item.cabang}</span>
                  <span className="nod-live-text">{item.driver || "-"}</span>
                  <span className="nod-live-plate">{item.noPolisi || "-"}</span>
                  <span className="nod-live-text">{item.sekolah || "-"}</span>
                </div>
              ))}
              {(dashboard.liveFeed || []).length === 0 && !loading ? (
                <div className="nod-live-empty">Belum ada update</div>
              ) : null}
            </div>
          </div>
        </section>

        <section className="nod-chart-grid">
        <Panel title="TREND SURAT JALAN 30 HARI TERAKHIR">
          <ResponsiveContainer width="100%" height={260}>
            <ComposedChart data={chartData} margin={{ top: 10, right: 8 }}>
              <CartesianGrid stroke="#19364d" vertical={false} />
              <XAxis
                dataKey="label"
                tick={{ fill: "#d3e4f2", fontSize: 11 }}
                axisLine={{ stroke: "#244d6a" }}
                tickLine={false}
                interval={4}
              />
              <YAxis
                tick={{ fill: "#d3e4f2", fontSize: 11 }}
                axisLine={false}
                tickLine={false}
                tickFormatter={formatNumber}
              />
              <Tooltip content={<DashboardTooltip />} />
              <Legend wrapperStyle={{ color: "#d8e7f2", fontSize: 12 }} />
              <Bar
                name="Sukses"
                dataKey="sukses"
                stackId="sj"
                fill={colors.success}
                radius={[3, 3, 0, 0]}
              />
              <Bar
                name="Pending"
                dataKey="pending"
                stackId="sj"
                fill={colors.pending}
                radius={[3, 3, 0, 0]}
              />
              <Line
                name="Rata-rata 30 hari"
                type="monotone"
                dataKey="rataRata30Hari"
                stroke={colors.average}
                strokeWidth={3}
                dot={{ r: 3, strokeWidth: 2, fill: "#062033" }}
              />
            </ComposedChart>
          </ResponsiveContainer>
        </Panel>

        <Panel title="TREND SDM AKTIF 30 HARI TERAKHIR">
          <ResponsiveContainer width="100%" height={260}>
            <ComposedChart
              data={dashboard.trendSdm || []}
              margin={{ top: 10, right: 8 }}
            >
              <CartesianGrid stroke="#19364d" vertical={false} />
              <XAxis
                dataKey="label"
                tick={{ fill: "#d3e4f2", fontSize: 11 }}
                axisLine={{ stroke: "#244d6a" }}
                tickLine={false}
                interval={4}
              />
              <YAxis
                tick={{ fill: "#d3e4f2", fontSize: 11 }}
                axisLine={false}
                tickLine={false}
              />
              <Tooltip content={<DashboardTooltip />} />
              <Legend wrapperStyle={{ color: "#d8e7f2", fontSize: 12 }} />
              <Line
                name="Driver Aktif"
                type="monotone"
                dataKey="driverAktif"
                stroke={colors.driver}
                strokeWidth={4}
                dot={{ r: 3, strokeWidth: 2, fill: "#062033" }}
              />
              <Line
                name="Helper Aktif"
                type="monotone"
                dataKey="helperAktif"
                stroke={colors.helper}
                strokeWidth={4}
                dot={{ r: 3, strokeWidth: 2, fill: "#062033" }}
              />
            </ComposedChart>
          </ResponsiveContainer>
        </Panel>

        <Panel title="TREND ARMADA AKTIF 12 BULAN TERAKHIR">
          <ResponsiveContainer width="100%" height={260}>
            <BarChart
              data={dashboard.fleetTrend || []}
              margin={{ top: 10, right: 8 }}
            >
              <CartesianGrid stroke="#19364d" vertical={false} />
              <XAxis
                dataKey="label"
                tick={{ fill: "#d3e4f2", fontSize: 11 }}
                axisLine={{ stroke: "#244d6a" }}
                tickLine={false}
              />
              <YAxis
                tick={{ fill: "#d3e4f2", fontSize: 11 }}
                axisLine={false}
                tickLine={false}
              />
              <Tooltip content={<DashboardTooltip />} />
              <Legend wrapperStyle={{ color: "#d8e7f2", fontSize: 12 }} />
              <Bar name="Motor" dataKey="motor" stackId="fleet" fill={colors.motor} />
              <Bar
                name="Grandmax"
                dataKey="grandmax"
                stackId="fleet"
                fill={colors.grandmax}
              />
              <Bar name="CDD" dataKey="cdd" stackId="fleet" fill={colors.cdd} />
              <Bar
                name="CDD Long"
                dataKey="cddLong"
                stackId="fleet"
                fill={colors.cddLong}
              />
              <Bar
                name="Tronton"
                dataKey="tronton"
                stackId="fleet"
                fill={colors.tronton}
              />
            </BarChart>
          </ResponsiveContainer>
        </Panel>

        <Panel
          title="JAM OPERASIONAL HARI INI"
          subtitle="Jumlah SJ sukses (sampai di tempat tujuan)"
        >
          <ResponsiveContainer width="100%" height={260}>
            <BarChart
              data={dashboard.hourly || []}
              layout="vertical"
              margin={{ top: 4, right: 28, bottom: 0, left: 12 }}
            >
              <CartesianGrid stroke="#19364d" horizontal={false} />
              <XAxis
                type="number"
                tick={{ fill: "#d3e4f2", fontSize: 11 }}
                axisLine={false}
                tickLine={false}
              />
              <YAxis
                type="category"
                dataKey="label"
                tick={{ fill: "#d3e4f2", fontSize: 12 }}
                axisLine={false}
                tickLine={false}
                width={48}
              />
              <Tooltip content={<DashboardTooltip />} />
              <Bar
                name="SJ Sukses"
                dataKey="sukses"
                fill={colors.success}
                radius={[0, 4, 4, 0]}
                barSize={16}
                label={{
                  position: "right",
                  fill: "#e8f4ff",
                  fontSize: 12,
                  formatter: formatNumber,
                }}
              />
            </BarChart>
          </ResponsiveContainer>
        </Panel>
        </section>

        <section className="nod-table-panel">
        <div className="nod-table-title">
          <div>
            <h2>PERFORMA CABANG HARI INI</h2>
            <span>Diurutkan berdasarkan % Selesai Terendah</span>
          </div>
          <div className="nod-progress-legend">
            <b>Progress:</b>
            <span>
              <i className="is-critical" /> &lt; 60%
            </span>
            <span>
              <i className="is-warning" /> 60% - 79%
            </span>
            <span>
              <i className="is-attention" /> 80% - 94%
            </span>
            <span>
              <i className="is-good" /> &ge; 95%
            </span>
          </div>
        </div>

        <div className="nod-table-wrap">
          <table className="nod-table">
            <thead>
              <tr>
                <th>NO</th>
                <th>SPV</th>
                <th>NAMA CABANG</th>
                <th>UNIT BERTUGAS</th>
                <th>DRIVER MASUK</th>
                <th>HELPER MASUK</th>
                <th>SJ DIBUAT</th>
                <th>SJ BERJALAN</th>
                <th>SJ SELESAI</th>
                <th>PENDING</th>
                <th>% SELESAI</th>
                <th>STATUS</th>
                <th>LAST UPDATE</th>
              </tr>
            </thead>
            <tbody>
              {(dashboard.branches || []).map((row) => (
                <tr
                  key={`${row.no}-${row.namaCabang}`}
                  className={statusClassMap[row.status] || ""}
                >
                  <td>{row.no}</td>
                  <td>{row.spv}</td>
                  <td className="nod-branch-name">{row.namaCabang}</td>
                  <td>{formatNumber(row.unitBertugas)}</td>
                  <td>{formatNumber(row.driverMasuk)}</td>
                  <td>{formatNumber(row.helperMasuk)}</td>
                  <td>{formatNumber(row.sjDibuat)}</td>
                  <td>{formatNumber(row.sjBerjalan)}</td>
                  <td>{formatNumber(row.sjSelesai)}</td>
                  <td>{formatNumber(row.pendingTotal)}</td>
                  <td className="nod-percent">{formatPercent(row.persenSelesai)}</td>
                  <td>
                    <StatusDot status={row.status} />
                  </td>
                  <td>{getLastUpdate(row.lastUpdate)}</td>
                </tr>
              ))}
              {(dashboard.branches || []).length === 0 && !loading ? (
                <tr>
                  <td colSpan="13" className="nod-empty">
                    Tidak ada data hari ini
                  </td>
                </tr>
              ) : null}
            </tbody>
            <tfoot>
              <tr>
                <td colSpan="2">TOTAL</td>
                <td>{formatNumber(totals.cabang)} CABANG</td>
                <td>{formatNumber(totals.unitBertugas)}</td>
                <td>{formatNumber(totals.driverMasuk)}</td>
                <td>{formatNumber(totals.helperMasuk)}</td>
                <td>{formatNumber(totals.sjDibuat)}</td>
                <td>{formatNumber(totals.sjBerjalan)}</td>
                <td>{formatNumber(totals.sjSelesai)}</td>
                <td>{formatNumber(totals.pendingTotal)}</td>
                <td className="nod-total-percent">
                  {formatPercent(totals.persenSelesai)}
                </td>
                <td>-</td>
                <td>-</td>
              </tr>
            </tfoot>
          </table>
        </div>

        <div className="nod-footnote">
          <span>Keterangan: % Selesai = (SJ Selesai / SJ Dibuat) x 100%</span>
          <span>SJ Berjalan = dibawa driver + muat + perjalanan + bongkar</span>
        </div>
        </section>
      </div>

      {loading ? (
        <div className="nod-loading">
          <SyncOutlined spin />
          <span>Memuat National Operation Dashboard...</span>
        </div>
      ) : null}
    </div>
  );
};

export default NationalOperationDashboard;
